import { Component, ErrorInfo, ReactNode } from 'react';

import { AlertTriangleIcon, RefreshCwIcon } from 'lucide-react';
import { Box, Stack, Typography } from '@mui/material';
import { Button } from '@repo/ui';

import { ROUTES } from 'routes';
import { buildErrorReportMailto, ERROR_REPORT_EMAIL } from 'constants/support';
import { IconBadge } from './IconBadge';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback. Defaults to the centred "something went wrong" screen. */
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * General render-crash boundary for the whole routed surface.
 *
 * Without this, a single bad render — a missing import throwing
 * `ReferenceError`, an undefined field dereferenced in a mapper — unmounts the
 * entire React tree and leaves a white screen with nothing but a console
 * message. This catches it and gives the user a recoverable surface instead.
 *
 * Modelled on `components/auth/AuthErrorBoundary` (same class shape), but this
 * one is full-screen rather than sized to sit inside the sign-in card, and its
 * "Try again" resets boundary state (a re-render) rather than reloading — a
 * reload is the heavier fallback offered by "Back to certificates".
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = { hasError: false, error: null };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Render crashed:', error, errorInfo.componentStack);
  }

  /**
   * Clear the error and re-render children. Recovers when the throw was
   * transient (a race, a momentarily-undefined field). If the same render
   * throws again the boundary simply catches it again — no worse than before.
   */
  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  /**
   * Hard navigation, not `navigate()` — a class component has no router hook,
   * and a full reload is the point: it rebuilds the tree from scratch, so it
   * recovers even when the crash left module-level or store state inconsistent.
   */
  private handleGoToCertificates = () => {
    window.location.assign(ROUTES.CERTIFICATES);
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 3,
            py: 6,
            width: '100%',
          }}
        >
          <Stack spacing={3} alignItems='center' textAlign='center' sx={{ maxWidth: 360 }}>
            {/*
              The shared badge (§36). This was a 56px red-tinted disc with a
              HARDCODED `#f44336` glyph — byte-identical to the copy in
              `auth/AuthErrorBoundary`, and one of five hand-rolls of the same
              object. The disc is now grey on every screen and the state lives
              in the glyph's tone, which also means the colour finally routes
              through the tenant's theme instead of a literal hex.
            */}
            <IconBadge icon={AlertTriangleIcon} tone='alert' />

            <Stack spacing={1}>
              <Typography variant='h6'>Something went wrong</Typography>
              <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.5 }}>
                This page hit an unexpected error. Try again. If it keeps happening, head back
                to your certificates.
              </Typography>
            </Stack>

            {/* Developers see what a user cannot. Never rendered in a production
                build — an error message can carry ids, tokens or internal paths. */}
            {import.meta.env.DEV && this.state.error ? (
              <Box
                component='pre'
                sx={{
                  width: '100%',
                  maxHeight: 200,
                  overflow: 'auto',
                  textAlign: 'left',
                  fontSize: 11,
                  lineHeight: 1.4,
                  p: 1.5,
                  m: 0,
                  borderRadius: 1,
                  bgcolor: 'rgba(0, 0, 0, 0.04)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {this.state.error.stack || this.state.error.message}
              </Box>
            ) : null}

            <Stack spacing={1.5} sx={{ width: '100%' }}>
              <Button variant='primary' onClick={this.handleRetry} fullWidth>
                <RefreshCwIcon size={16} className='mr-2' />
                Try again
              </Button>
              <Button variant='secondary' onClick={this.handleGoToCertificates} fullWidth>
                Back to certificates
              </Button>
            </Stack>

            {/*
              A way to TELL SOMEONE, which this screen had none of.

              Until now a crash was a dead end with two buttons that both just
              re-run the thing that broke: if "Try again" throws again, the
              reader's only remaining options are to leave or to give up, and
              nobody at Vera ever learns the page is broken. There is no error
              sink in this app (see `buildErrorReportMailto`), so this link is
              currently the ONLY channel by which a crash becomes known.

              A text link rather than a third Button, deliberately. The two
              buttons above are the RECOVERY path and should stay the obvious
              thing to do; writing an email is the fallback for when recovery
              has failed. Giving it equal weight would make a two-choice screen
              a three-choice one and push the primary action further from the
              eye — this is the same one-primary rule §26 applies to a header,
              read for a decision screen.

              It goes to VERA, not to the brand whose site this is. A render
              crash is Vera's defect; the reasoning, and why that reverses the
              recommendation that was on file, is on `ERROR_REPORT_EMAIL`.
            */}
            <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.5 }}>
              Still stuck?{' '}
              <Box
                component='a'
                href={buildErrorReportMailto(this.state.error)}
                sx={{ color: 'inherit', textDecoration: 'underline' }}
              >
                Email {ERROR_REPORT_EMAIL}
              </Box>
            </Typography>
          </Stack>
        </Box>
      );
    }

    return this.props.children;
  }
}
